/**
 * 登录身份的**展示映射**（`specs/15` §2）。
 *
 * 只做「身份 → 中文标签 / 头像首字」，不参与权限判断——权限由后端决定（CONSTITUTION A3）。
 * 顶部与侧栏共用同一处映射，避免"同一份角色清单写两遍"（两份必然漂移）。
 */

import type { SessionUser } from "@/api/auth";

/**
 * 创作子角色的中文标签（**唯一一份**）。
 *
 * ⚠️ `admin` 也在这里：它不是可授予的子角色（`ADR-025` D3），而是首个账号的引导值，
 * 但**确实会出现在已存在的账号上**，故展示层必须认得它。
 */
export const CREATION_ROLE_LABEL: Record<string, string> = {
  head_writer: "总编剧",
  writer: "编剧",
  designer: "设计",
  reviewer: "审阅",
  admin: "管理员",
};

/** `roleLabel` 的单一实现：`/account` 与 `lib/session` 共用，避免标签漂移。 */
export function roleLabel(user: SessionUser | null | undefined): string {
  if (!user) return "未登录";
  if (user.platform_role === "player") return "玩家";
  if (!user.creation_role) return "编辑";
  return CREATION_ROLE_LABEL[user.creation_role] ?? user.creation_role;
}

/**
 * **当前身份**（`view_mode`）的中文标签（`ADR-025` D1）。
 *
 * ⚠️ 与 `roleLabel` 是**两个不同的东西**：那个答"他被授权做什么"，这个答"他此刻看哪一侧"。
 * 合用同一个函数就是本轮 `ADR-025` 要拆列的那件事本身。
 */
export function viewModeLabel(viewMode: string): string {
  return viewMode === "editor" ? "编剧" : "观众";
}

export function initialOf(user: SessionUser): string {
  const source = user.display_name || user.email;
  return source.slice(0, 1).toUpperCase();
}
